// Push Notification utility for order status updates

// Platform detection — iOS Safari in a regular tab does NOT support
// the Web Notifications API at all. iOS 16.4+ supports Web Push, but only
// when the site is installed as a PWA (Add to Home Screen).
const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
const isAndroid = /Android/i.test(ua);
const isStandalone = typeof window !== 'undefined' && (
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true
);

export const NotificationService = {
  // Check if notifications are supported in this runtime
  isSupported: () => {
    if (typeof window === 'undefined') return false;
    if (!('Notification' in window)) return false;
    // iOS Safari exposes Notification but it's useless in a regular tab —
    // only works when installed as a PWA (iOS 16.4+).
    if (isIOS && !isStandalone) return false;
    return true;
  },

  // Platform capability info for UI hints
  getPlatformInfo: () => ({
    isIOS,
    isAndroid,
    isStandalone,
    canInstallPWA: isIOS && !isStandalone, // iOS user could Add to Home Screen
    supported: NotificationService.isSupported(),
  }),

  // Request permission for notifications
  requestPermission: async () => {
    if (!NotificationService.isSupported()) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  },

  // Check current permission status. Returns:
  //   'granted' | 'denied' | 'default' | 'unsupported' | 'ios-needs-pwa'
  getPermissionStatus: () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (isIOS && !isStandalone) return 'ios-needs-pwa';
    return Notification.permission;
  },

  // Send a notification
  notify: (title, options = {}) => {
    if (!NotificationService.isSupported()) return null;
    if (Notification.permission !== 'granted') return null;

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      tag: 'ashandmerch-notification',
      renotify: true,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      notification.onclick = () => {
        window.focus();
        if (options.onClick) options.onClick();
        notification.close();
      };

      // Auto close after 5 seconds
      setTimeout(() => notification.close(), 5000);
      
      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  },

  // Order status notifications
  notifyOrderStatus: (orderId, status, driverName = null) => {
    const shortId = orderId.slice(0, 8).toUpperCase();
    
    const statusMessages = {
      confirmed: {
        title: '✅ Order Confirmed',
        body: `Order #${shortId} has been confirmed and is being prepared.`
      },
      preparing: {
        title: '📦 Order Being Prepared',
        body: `Order #${shortId} is being prepared for delivery.`
      },
      out_for_delivery: {
        title: '🚚 Driver On The Way!',
        body: `${driverName || 'Your driver'} is on the way with order #${shortId}.`
      },
      delivered: {
        title: '🎉 Order Delivered!',
        body: `Order #${shortId} has been delivered. Enjoy!`
      },
      cancelled: {
        title: '❌ Order Cancelled',
        body: `Order #${shortId} has been cancelled.`
      }
    };

    const message = statusMessages[status];
    if (message) {
      return NotificationService.notify(message.title, { body: message.body });
    }
    return null;
  },

  // Driver assignment notification
  notifyDriverAssigned: (orderId, driverName) => {
    const shortId = orderId.slice(0, 8).toUpperCase();
    return NotificationService.notify('🚗 Driver Assigned', {
      body: `${driverName} has been assigned to your order #${shortId}.`
    });
  },

  // New order notification (for drivers)
  notifyNewAssignment: (orderId, customerName) => {
    const shortId = orderId.slice(0, 8).toUpperCase();
    return NotificationService.notify('📋 New Delivery Assignment', {
      body: `New delivery to ${customerName} - Order #${shortId}`
    });
  }
};

export default NotificationService;
